export class Authorization {
  /**
   * Create a new authorization for the specified user
   *
   * @param {object} user
   */
  constructor(user) {
    switch (user?.role) {
    case 'ADMIN':
      this.rules = ADMIN_RULES;
      break;
    case 'MANAGER':
      this.rules = MANAGER_RULES;
      break;
    case 'THEATER_MANAGER':
      this.rules = THEATER_MANAGER_RULES;
      break;
    case 'CREATIVE':
      this.rules = CREATIVE_RULES;
      break;
    default:
      this.rules = new Map();
    }
    this.override_rules = new Map();

    // check flags
    for (const flag of (user?.roleFlags || [])) {
      if (flag in EXTRA_FLAG_RULES) {
        for (const scope of [user?.role, '*']) {
          if (scope in EXTRA_FLAG_RULES[flag]) {
            for (const entry of EXTRA_FLAG_RULES[flag][scope]) {
              this.override_rules.set(entry[0], entry[1]);
            }
          }
        }
      }
    }
  }

  runRule(rule, ...params) {
    if (typeof rule === 'function') {
      return rule(...params);
    } else {
      return !!rule;
    }
  }

  findRule(action) {
    if (this.override_rules.has(action)) {
      return [2, this.override_rules.get(action)];
    } else if (this.rules.has(action)) {
      return [1, this.rules.get(action)];
    }
    return [0, false];
  }

  can(action, ...params) {
    const action_list = [action, '*'];
    for (const a of action_list) {
      let [level, rule] = this.findRule(a);
      if (level > 0) {
        return this.runRule(rule, ...params);
      }
    }
    return false;
  }
}

function build_rules(ruleset) {
  const rules = new Map();
  for (const rule in ruleset) {
    rules.set(rule, ruleset[rule]);
  }
  return rules;
}

export const MANAGER_RULES = build_rules({
  'actors.list': true,
  'actors.view_resume': true,
  'actors.view_skills': true,
  'actors.view_tags': true,
  'actors.view_shows': true,
  'actors.view_additional_info': true,
  'actors.view_dates': true,
  'actors.edit': true,
  'actors.delete': true,
  'actor_headshots.list': true,
  'actor_headshots.create': true,
  'actor_headshots.edit': true,
  'actor_notes.list': true,
  'actor_notes.create': true,
  'actor_notes.edit': true,
  'actor_notes.delete': true,
  'actor_videos.list': true,
  'actor_videos.create': true,
  'actor_videos.edit': true,
  'note_topics.list': true,
  'note_topics.create': true,
  'note_topics.edit': true,
  'note_topics.delete': true,
  'prefab_filters.list': true,
  'prefab_filters.create': true,
  'prefab_filters.edit': true,
  'prefab_filters.delete': true,
  'seasons.list': true,
  'seasons.create': true,
  'seasons.edit': true,
  'seasons.delete': true,
  'shows.list': true,
  'shows.create': true,
  'shows.edit': true,
  'shows.delete': true,
  'shows.view_any_assigned_role': true,
  'show_documents.list': true,
  'show_documents.create': true,
  'show_documents.edit': true,
  'show_documents.delete': true,
  'show_roles.list': true,
  'show_roles.create': true,
  'show_roles.edit': true,
  'show_roles.assign_actor': true,
  'show_roles.delete': true,
  'show_roles.view_filters': true,
  // 'show_roles.view_contract': true,
  // 'show_roles.edit_contract': true,
  'show_role_prospects.list': true,
  'show_role_prospects.create': true,
  'show_role_prospects.edit': true,
  'show_role_prospects.delete': true,
  'show_role_prospect_notes.list': true,
  'show_role_prospect_notes.create': true,
  'show_role_prospect_notes.edit': true,
  'show_role_prospect_notes.delete': true,
  'skills.list': true,
  'skills.create': true,
  'skills.edit': true,
  'skills.delete': true,
  'tags.list': true,
  'tags.create': true,
  'tags.edit': true,
  'tags.delete': true,
});

export const ADMIN_RULES = build_rules({
  '*': true,
  'show_role_prospects.edit.creative': false,
});

export const THEATER_MANAGER_RULES = build_rules({
  'seasons.list': true,
  'actor_headshots.list': true,
  'show_documents.list': true,
});

export const EXTRA_FLAG_RULES = {
  'view_contract_details': {
    '*': build_rules({
      'show_roles.view_contract': true,
    }),
  },
  'manage_contract_details': {
    '*': build_rules({
      'show_roles.view_contract': true,
      'show_roles.edit_contract': true,
    }),
  },
  'view_payroll_details': {
    '*': build_rules({
      'show_roles.view_payroll': true,
    }),
  },
  'manage_payroll_details': {
    '*': build_rules({
      'show_roles.view_payroll': true,
      'show_roles.edit_payroll': true,
    }),
  },
};

export const CREATIVE_RULES = build_rules({
  'actors.list': true,
  'actors.view_resume': true,
  'actors.view_skills': true,
  'actors.view_tags': true,
  'actor_headshots.list': true,
  'actor_videos.list': true,
  'seasons.list': true,
  'shows.list': true,
  'shows.view_any_assigned_role': true,
  'show_documents.list': true,
  'show_roles.list': true,
  'show_roles.view_filters': true,
  'show_roles.assign_actor': true,
  'show_role_prospects.list': true,
  'show_role_prospects.create': true,
  'show_role_prospects.edit': true,
  'show_role_prospects.edit.creative': true,
  'show_role_prospect_notes.list': true,
  'show_role_prospect_notes.create': true,
  'show_role_prospect_notes.create.creative': true,
  'show_role_prospect_notes.edit': true,
  'show_role_prospect_notes.delete': true,
});
